// STRATEGIST — per-person ENGAGEMENT-STRATEGY brief drawer for the human sales
// team. Given a lead/prospect, it shows EXACTLY how to approach THIS rich,
// high-status person: the read, the fit score, the best path in, the touch
// sequence, the (human-executed) LinkedIn play, the psychology, the make-or-
// break first-impression opener, likely objections + rebuttals, and a do-not
// list. ADVISORY ONLY — nothing here automates or scrapes; a human executes
// every touch by hand. BACK_STAGE_ONLY (prospect, not client — WALL not crossed).
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  X,
  Sparkles,
  Eye,
  Target,
  Route,
  Linkedin,
  Brain,
  MessageSquareQuote,
  ShieldAlert,
  HelpCircle,
  RefreshCw,
  AlertTriangle,
  Quote,
} from "lucide-react";

import { strategist } from "@/lib/api";

const BTN_PRIMARY_STYLE = {
  background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))",
  color: "rgb(252,249,243)",
};
const BTN_SECONDARY_STYLE = {
  background: "rgba(244,239,230,0.9)",
  border: "1px solid rgba(190,176,152,0.6)",
  color: "rgb(38,31,24)",
};

// The brief shape may vary a little depending on the LLM output / route
// version. Read every field defensively so a partial brief never paints blank
// sections and never throws. Returns a normalized, render-ready object.
export function normalizeBrief(raw) {
  if (!raw || typeof raw !== "object") return null;
  // Unwrap a { brief: {...} } envelope if the route returns one.
  const b = raw.brief && typeof raw.brief === "object" ? { ...raw.brief, ...raw } : raw;
  const arr = (...vals) => {
    for (const v of vals) if (Array.isArray(v) && v.length) return v;
    return [];
  };
  const str = (...vals) => {
    for (const v of vals) if (typeof v === "string" && v.trim()) return v.trim();
    return "";
  };
  const fitRaw = b.fit_score ?? b.fit?.score ?? b.score;
  const fit = typeof fitRaw === "number" ? Math.max(0, Math.min(100, Math.round(fitRaw))) : null;

  return {
    status: str(b.status) || "ready",
    name: str(b.full_name, b.name, b.target_name, b.person),
    title: str(b.title, b.headline, b.role),
    company: str(b.company, b.organization, b.target_domain),
    read: str(b.the_read, b.read, b.summary, b.headline_read),
    fitScore: fit,
    fitWhy: str(b.fit_why, b.fit?.why, b.fit_reason, b.why_fit),
    bestPath: {
      channel: str(b.best_path?.channel, b.best_channel, b.channel),
      who: str(b.best_path?.who, b.contact_first, b.who_to_contact, b.first_contact),
      why: str(b.best_path?.why, b.best_path_why, b.path_why),
    },
    sequence: arr(b.sequence, b.touch_sequence, b.steps).map((s, i) =>
      typeof s === "string"
        ? { step: i + 1, channel: "", timing: "", action: s, purpose: "" }
        : {
            step: s.step ?? i + 1,
            channel: str(s.channel, s.medium),
            timing: str(s.timing, s.when, s.day),
            action: str(s.action, s.what, s.do),
            purpose: str(s.purpose, s.why, s.goal),
          },
    ),
    linkedin: (() => {
      const lp = b.linkedin_play || b.linkedin || {};
      const engage = str(lp.engage, lp.what_to_engage, b.linkedin_engage);
      const angle = str(lp.comment_angle, lp.angle, b.linkedin_angle);
      const avoid = arr(lp.avoid, lp.what_not_to_do, lp.donts, b.linkedin_avoid);
      if (!engage && !angle && !avoid.length) return null;
      return { engage, angle, avoid };
    })(),
    psychology: arr(b.psychology, b.psych, b.psychology_notes),
    opener: str(b.opener, b.first_impression_opener, b.first_opener, b.opening_line),
    objections: arr(b.objections, b.likely_objections).map((o) =>
      typeof o === "string"
        ? { objection: o, rebuttal: "" }
        : {
            objection: str(o.objection, o.concern, o.q),
            rebuttal: str(o.rebuttal, o.response, o.answer, o.a),
          },
    ),
    doNot: arr(b.do_not, b.donts, b.do_not_list, b.never).map((d) =>
      typeof d === "string" ? d : str(d.text, d.rule, d.item),
    ),
    provider: str(b.provider),
    model: str(b.model),
  };
}

function SectionHead({ icon: Icon, children, tone }) {
  return (
    <div
      className="mono text-[10px] uppercase tracking-[0.16em] mb-2.5 flex items-center gap-1.5"
      style={{ color: tone || "rgb(var(--gold))" }}
    >
      <Icon size={12} className="shrink-0" />
      {children}
    </div>
  );
}

// FIT — a big, confident score 0-100 with a colour that earns its place.
function fitColor(score) {
  if (score == null) return "rgb(var(--muted-2))";
  if (score >= 75) return "rgb(32,138,78)";
  if (score >= 50) return "rgb(176,116,18)";
  return "rgb(196,52,44)";
}

function BriefBody({ brief }) {
  const b = brief;
  if (b.status === "stub") {
    return (
      <div
        className="pj-card p-5 flex items-start gap-3"
        data-testid="strategy-stub-notice"
        style={{ borderColor: "rgba(176,116,18,0.4)" }}
      >
        <AlertTriangle size={16} className="shrink-0 mt-0.5" style={{ color: "rgb(176,116,18)" }} />
        <div className="mono text-[12px] text-[rgb(var(--dim))] leading-relaxed">
          <div className="text-[rgb(var(--fg))] mb-1 uppercase tracking-[0.12em] text-[11px]">
            Brief not generated
          </div>
          Set <code>ANTHROPIC_API_KEY</code> on the server to generate real engagement-strategy
          briefs. The strategist returned a stub.
        </div>
      </div>
    );
  }
  if (b.status === "pending") {
    return (
      <div className="pj-card p-5 mono text-[12px] text-[rgb(var(--dim))]" data-testid="strategy-pending-notice">
        This brief is still being generated. Click Regenerate in a moment.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="strategy-sections">
      {/* THE READ */}
      {b.read && (
        <section data-testid="strategy-read">
          <SectionHead icon={Eye}>The read</SectionHead>
          <p className="mono text-[13px] leading-relaxed text-[rgb(var(--fg))]">{b.read}</p>
        </section>
      )}

      {/* FIT — big score + why */}
      <section className="pj-card p-4" data-testid="strategy-fit">
        <SectionHead icon={Target}>Fit</SectionHead>
        <div className="flex items-start gap-4">
          <div className="shrink-0 text-center">
            <div
              className="font-display leading-none"
              style={{ fontSize: 44, color: fitColor(b.fitScore) }}
              data-testid="strategy-fit-score"
            >
              {b.fitScore != null ? b.fitScore : "—"}
            </div>
            <div className="pj-label mt-1">/ 100</div>
          </div>
          {b.fitWhy && (
            <p className="mono text-[12px] leading-relaxed text-[rgb(var(--dim))] flex-1 min-w-0">
              {b.fitWhy}
            </p>
          )}
        </div>
      </section>

      {/* BEST PATH */}
      {(b.bestPath.channel || b.bestPath.who || b.bestPath.why) && (
        <section data-testid="strategy-best-path">
          <SectionHead icon={Route}>Best path</SectionHead>
          <div className="pj-card p-4 space-y-2">
            {b.bestPath.channel && (
              <div className="mono text-[12px]">
                <span className="pj-label">Channel</span>
                <div className="text-[rgb(var(--fg))] text-[14px] mt-0.5">{b.bestPath.channel}</div>
              </div>
            )}
            {b.bestPath.who && (
              <div className="mono text-[12px]">
                <span className="pj-label">Contact first</span>
                <div className="text-[rgb(var(--fg))] mt-0.5">{b.bestPath.who}</div>
              </div>
            )}
            {b.bestPath.why && (
              <p className="mono text-[12px] text-[rgb(var(--dim))] leading-relaxed">{b.bestPath.why}</p>
            )}
          </div>
        </section>
      )}

      {/* THE SEQUENCE */}
      {b.sequence.length > 0 && (
        <section data-testid="strategy-sequence">
          <SectionHead icon={Route}>The sequence</SectionHead>
          <ol className="space-y-2">
            {b.sequence.map((s, i) => (
              <li
                key={i}
                data-testid={`strategy-sequence-step-${i}`}
                className="pj-card p-3 flex gap-3"
              >
                <span
                  className="font-display shrink-0 leading-none"
                  style={{ fontSize: 20, color: "rgb(var(--gold))", minWidth: 22 }}
                >
                  {s.step}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mono text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--muted-2))] flex flex-wrap gap-x-2 gap-y-0.5">
                    {s.channel && <span className="text-[rgb(var(--gold))]">{s.channel}</span>}
                    {s.timing && <span>· {s.timing}</span>}
                  </div>
                  {s.action && (
                    <div className="mono text-[12.5px] text-[rgb(var(--fg))] mt-1 leading-snug">
                      {s.action}
                    </div>
                  )}
                  {s.purpose && (
                    <div className="mono text-[11px] text-[rgb(var(--dim))] mt-1 leading-snug">
                      {s.purpose}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* LINKEDIN PLAY — human-executed; only if applicable */}
      {b.linkedin && (
        <section data-testid="strategy-linkedin">
          <SectionHead icon={Linkedin}>LinkedIn play · human-executed</SectionHead>
          <div className="pj-card p-4 space-y-2">
            {b.linkedin.engage && (
              <div className="mono text-[12px]">
                <span className="pj-label">Engage with</span>
                <div className="text-[rgb(var(--fg))] mt-0.5 leading-relaxed">{b.linkedin.engage}</div>
              </div>
            )}
            {b.linkedin.angle && (
              <div className="mono text-[12px]">
                <span className="pj-label">Comment angle</span>
                <div className="text-[rgb(var(--fg))] mt-0.5 leading-relaxed">{b.linkedin.angle}</div>
              </div>
            )}
            {b.linkedin.avoid.length > 0 && (
              <div className="mono text-[12px]">
                <span className="pj-label" style={{ color: "rgb(196,52,44)" }}>
                  What not to do
                </span>
                <ul className="mt-1 space-y-1">
                  {b.linkedin.avoid.map((a, i) => (
                    <li
                      key={i}
                      data-testid={`strategy-linkedin-avoid-${i}`}
                      className="text-[rgb(var(--dim))] leading-snug"
                    >
                      · {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* PSYCHOLOGY */}
      {b.psychology.length > 0 && (
        <section data-testid="strategy-psychology">
          <SectionHead icon={Brain}>Psychology</SectionHead>
          <ul className="space-y-1.5">
            {b.psychology.map((p, i) => (
              <li
                key={i}
                data-testid={`strategy-psych-${i}`}
                className="mono text-[12px] text-[rgb(var(--fg))] leading-relaxed flex gap-2"
              >
                <span className="text-[rgb(var(--gold))] shrink-0">·</span>
                <span className="min-w-0">{p}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FIRST-IMPRESSION OPENER — the make-or-break line, visually emphasized */}
      {b.opener && (
        <section data-testid="strategy-opener">
          <SectionHead icon={MessageSquareQuote}>First-impression opener</SectionHead>
          <blockquote
            className="relative p-4 pl-10"
            style={{
              background: "rgba(169,130,28,0.08)",
              borderLeft: "3px solid rgb(var(--gold))",
              borderRadius: 2,
            }}
          >
            <Quote
              size={18}
              className="absolute left-3 top-4 opacity-50"
              style={{ color: "rgb(var(--gold))" }}
            />
            <p
              className="text-[15px] leading-relaxed text-[rgb(var(--fg))]"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              “{b.opener}”
            </p>
          </blockquote>
          <p className="mono text-[10px] text-[rgb(var(--muted-2))] mt-1.5">
            This is the make-or-break line. Deliver it as your own — do not paste verbatim.
          </p>
        </section>
      )}

      {/* LIKELY OBJECTIONS — objection -> rebuttal pairs */}
      {b.objections.length > 0 && (
        <section data-testid="strategy-objections">
          <SectionHead icon={HelpCircle}>Likely objections</SectionHead>
          <div className="space-y-2">
            {b.objections.map((o, i) => (
              <div key={i} data-testid={`strategy-objection-${i}`} className="pj-card p-3">
                <div className="mono text-[12px] text-[rgb(var(--fg))] flex gap-2">
                  <span className="pj-label shrink-0 mt-0.5" style={{ minWidth: 16 }}>
                    Q
                  </span>
                  <span className="min-w-0">{o.objection}</span>
                </div>
                {o.rebuttal && (
                  <div className="mono text-[12px] text-[rgb(var(--dim))] flex gap-2 mt-1.5">
                    <span
                      className="pj-label shrink-0 mt-0.5"
                      style={{ minWidth: 16, color: "rgb(32,138,78)" }}
                    >
                      A
                    </span>
                    <span className="min-w-0 leading-relaxed">{o.rebuttal}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* DO NOT — red, warning-styled no-fuckup list */}
      {b.doNot.length > 0 && (
        <section data-testid="strategy-do-not">
          <SectionHead icon={ShieldAlert} tone="rgb(196,52,44)">
            Do not
          </SectionHead>
          <ul
            className="space-y-1.5 p-4"
            style={{
              background: "rgba(196,52,44,0.06)",
              border: "1px solid rgba(196,52,44,0.30)",
              borderRadius: 2,
            }}
          >
            {b.doNot.map((d, i) => (
              <li
                key={i}
                data-testid={`strategy-do-not-${i}`}
                className="mono text-[12px] leading-relaxed flex gap-2"
                style={{ color: "rgb(150,38,32)" }}
              >
                <ShieldAlert size={13} className="shrink-0 mt-0.5" />
                <span className="min-w-0">{d}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Provenance footer — advisory tool, human-executed. */}
      <p className="mono text-[10px] text-[rgb(var(--muted-2))] pt-1 border-t border-[rgb(var(--ln))]">
        Advisory only · human-executed · no automation or scraping.
        {(b.provider || b.model) && (
          <span> · {[b.provider, b.model].filter(Boolean).join(" · ")}</span>
        )}
      </p>
    </div>
  );
}

// The slide-in drawer. `lead` is the prospect row; the drawer fetches the
// latest brief on open and offers Generate / Regenerate.
export function StrategyBriefDrawer({ lead, onClose }) {
  const qc = useQueryClient();
  const leadId = lead?.id || lead?.lead_id;
  const name = lead?.full_name || lead?.name || "this prospect";
  const [generating, setGenerating] = useState(false);
  const [override, setOverride] = useState(null); // brief from a just-run generate

  // GET the latest brief for this lead (if one already exists). 404/null is fine.
  const { data, isLoading, error } = useQuery({
    queryKey: ["strategist-brief", leadId],
    queryFn: () => strategist.forLead(leadId),
    enabled: Boolean(leadId),
    retry: false,
  });

  const raw = override || data;
  const brief = normalizeBrief(raw);
  const hasBrief = Boolean(brief);

  const run = async () => {
    if (!leadId) return;
    setGenerating(true);
    try {
      const r = await strategist.generate(leadId);
      setOverride(r);
      qc.setQueryData(["strategist-brief", leadId], r);
      qc.invalidateQueries({ queryKey: ["strategist-briefs"] });
      const norm = normalizeBrief(r);
      if (norm?.status === "stub") {
        toast.message("Strategist returned a stub — set ANTHROPIC_API_KEY on the server.");
      } else {
        toast.success("Engagement strategy ready");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not generate the brief — try again.",
      );
    }
    setGenerating(false);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      data-testid={`strategy-drawer-${leadId || "x"}`}
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(20,17,13,0.45)" }}
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="relative h-full w-full max-w-[540px] overflow-y-auto p-6"
        style={{ background: "rgb(var(--surface))", borderLeft: "1px solid rgb(var(--ln))" }}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="min-w-0">
            <div className="pj-label mb-1 flex items-center gap-1.5">
              <Sparkles size={11} className="text-[rgb(var(--gold))]" />
              Engagement strategy
            </div>
            <h2 className="font-display text-[24px] leading-tight truncate" title={name}>
              {name}
            </h2>
            {(brief?.title || brief?.company || lead?.title || lead?.company) && (
              <div className="mono text-[11px] text-[rgb(var(--dim))] mt-0.5 truncate">
                {[brief?.title || lead?.title, brief?.company || lead?.company]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="strategy-drawer-close"
            className="p-1.5 hover:bg-[rgb(236,229,216)] shrink-0"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mb-5">
          {!hasBrief ? (
            <button
              type="button"
              onClick={run}
              disabled={generating || !leadId}
              data-testid="strategy-generate-btn"
              className="mono inline-flex items-center gap-1.5 px-4 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={BTN_PRIMARY_STYLE}
            >
              <Sparkles size={11} />
              {generating ? "Generating engagement strategy…" : "Generate engagement strategy"}
            </button>
          ) : (
            <button
              type="button"
              onClick={run}
              disabled={generating}
              data-testid="strategy-regenerate-btn"
              className="mono inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={BTN_SECONDARY_STYLE}
            >
              <RefreshCw size={11} className={generating ? "animate-spin" : ""} />
              {generating ? "Regenerating…" : "Regenerate"}
            </button>
          )}
        </div>

        {/* Body */}
        {generating && !hasBrief && (
          <div
            className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))] flex items-center gap-2"
            data-testid="strategy-loading"
          >
            <Sparkles size={14} className="animate-pulse text-[rgb(var(--gold))]" />
            Generating engagement strategy…
          </div>
        )}

        {!generating && isLoading && (
          <div className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))]" data-testid="strategy-fetching">
            Loading…
          </div>
        )}

        {!generating && !isLoading && !hasBrief && !error && (
          <div
            className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))] text-center"
            data-testid="strategy-empty"
          >
            No brief yet for {name}. Generate one to see exactly how to approach this person.
          </div>
        )}

        {!generating && !isLoading && !hasBrief && error && (
          <div
            className="pj-card p-6 mono text-[12px] text-center"
            data-testid="strategy-error"
            style={{ color: "rgb(var(--dim))" }}
          >
            No existing brief. Click Generate engagement strategy above.
          </div>
        )}

        {hasBrief && <BriefBody brief={brief} />}
      </aside>
    </div>
  );
}

export default StrategyBriefDrawer;
